import { useState } from "react";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { combineValidators, maxCharactersValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { ConditionField, ReportField } from "../../../../../../../../../../api/types/svTypes";
import { isCustomColumn } from "../../../../../../utilities/fieldsHelper";
import { getConditionFieldCaption } from "../controls/conditionSelectHelpers";

export interface ConditionValueFormDataFields {
  name: string;
  selectedConditionGuid: string;
}

interface FormState extends ConditionValueFormDataFields {
  isValid?: boolean;
  nameResult?: ValidationResult;
  conditionResult?: ValidationResult;
  touchedFields: (keyof ConditionValueFormDataFields)[];
}

const validateName = combineValidators(requiredValidator, maxCharactersValidator(255));

const validateForm = (form: FormState) => {
  const conditionResult = requiredValidator(form.selectedConditionGuid);
  const nameResult = validateName(form.name);
  const isValid = nameResult.isValid && conditionResult.isValid;
  return { isValid, nameResult, conditionResult };
};

const getInitialConditionGuid = (currentField: ReportField | undefined): string => {
  if (!currentField || !isCustomColumn(currentField)) {
    return "";
  }
  if (currentField.invalid) {
    return "";
  }
  return currentField.config.formulaParameters?.[0] ?? "";
};

const getInitialName = (currentField: ReportField | undefined, conditions: ConditionField[]): string => {
  if (currentField && isCustomColumn(currentField)) {
    return currentField.config.customLabel ?? currentField.config.name ?? "";
  }
  const referenced = conditions.find((c) => c.config.guid === getInitialConditionGuid(currentField));
  return referenced ? getConditionFieldCaption(referenced) : "";
};

const useSetupConditionValueMenu = (
  fieldGuid: string | undefined,
  aggregationFields: ReportField[],
  conditions: ConditionField[]
) => {
  const [form, setForm] = useState<FormState>(() => {
    const currentField = aggregationFields.find((field) => field.config.guid === fieldGuid);
    const initForm: FormState = {
      name: getInitialName(currentField, conditions),
      selectedConditionGuid: getInitialConditionGuid(currentField),
      touchedFields: [],
    };
    const validationResult = validateForm(initForm);
    return { ...initForm, ...validationResult };
  });

  const handleNameChange = (name: string) => {
    setForm((prev) => {
      const newForm: FormState = {
        ...prev,
        name,
        touchedFields: distinct([...prev.touchedFields, "name"]),
      };
      const validationResult = validateForm(newForm);
      return { ...newForm, ...validationResult };
    });
  };

  const handleSelectedConditionGuidChange = (selectedConditionGuid: string) => {
    setForm((prev) => {
      const isNameTouched = prev.touchedFields.includes("name");
      const referenced = conditions.find((c) => c.config.guid === selectedConditionGuid);
      const newName = isNameTouched ? prev.name : referenced ? getConditionFieldCaption(referenced) : prev.name;
      const newForm: FormState = {
        ...prev,
        selectedConditionGuid,
        name: newName,
        touchedFields: distinct([...prev.touchedFields, "selectedConditionGuid"]),
      };
      const validationResult = validateForm(newForm);
      return { ...newForm, ...validationResult };
    });
  };

  return {
    isValid: form.isValid,
    isDirty: form.touchedFields.length > 0,
    name: form.name,
    selectedConditionGuid: form.selectedConditionGuid,
    nameError: form.touchedFields.includes("name") ? form.nameResult?.error : undefined,
    selectedConditionGuidError: form.touchedFields.includes("selectedConditionGuid")
      ? form.conditionResult?.error
      : undefined,
    onNameChange: handleNameChange,
    onSelectedConditionGuidChange: handleSelectedConditionGuidChange,
  };
};

export default useSetupConditionValueMenu;
